import React, { useRef } from "react";

interface TabConfig {
  label: string;
  Component: React.ComponentType<any>;
  componentProps?: Record<string, any>;
  show: boolean;
  testId?: string;
}

interface Tab {
  label: string;
  component: React.ReactNode;
  show: boolean;
  testId?: string;
}

interface MessageKeys {
  title: string;
  description: string;
  cancelText: string;
  confirmText: string;
}

interface TabRef {
  current?: {
    hasChanges?: boolean | (() => boolean);
    resetChanges?: () => void;
  };
}

interface UseTabRefsWithDirtyCheckReturn {
  tabs: Tab[];
  tabRefs: React.MutableRefObject<TabRef[]>;
  beforeTabChange: (currentTab: number, nextTab: number) => Promise<boolean>;
}

/**
 * Centralized hook to build tab refs, inject refs into components and provide a beforeTabChange guard.
 *
 * @param tabsConfig - array of { label, Component, componentProps, show }
 * @param t - translation function
 * @param getUserConfirmation - function(message, callback)
 * @param messageKeys - optional translation keys for confirmation dialog
 */
export default function useTabRefsWithDirtyCheck(
  tabsConfig: TabConfig[],
  t: (key: string) => string,
  getUserConfirmation: (message: string, callback: (allow: boolean) => void) => void,
  messageKeys: MessageKeys
): UseTabRefsWithDirtyCheckReturn {
  const tabRefs = useRef<TabRef[]>([]);
  tabRefs.current = tabsConfig.map((_, i) => tabRefs.current[i] || React.createRef());

  const tabs: Tab[] = tabsConfig.map((cfg, i) => ({
    label: cfg.label,
    component: React.createElement(cfg.Component as any, { ...(cfg.componentProps || {}), ref: tabRefs.current[i] }),
    show: cfg.show,
    testId: cfg.testId,
  }));

  const beforeTabChange = async (currentTab: number, nextTab: number): Promise<boolean> => {
    try {
      const visibleOriginalIndices = tabs
        .map((tab, i) => (tab.show ? i : -1))
        .filter((i) => i >= 0);

      const originalIndex = visibleOriginalIndices[currentTab];
      const currentRef = tabRefs.current[originalIndex];

      // Detect hasChanges: supports both function and property
      const hasChanges =
        !!currentRef?.current &&
        (typeof currentRef.current.hasChanges === "function"
          ? currentRef.current.hasChanges()
          : currentRef.current.hasChanges);

      if (hasChanges) {
        const message = JSON.stringify({
          confirmationTitle: t(messageKeys.title),
          confirmation: t(messageKeys.description),
          cancelButtonText: t(messageKeys.cancelText),
          confirmButtonText: t(messageKeys.confirmText),
        });

        return await new Promise<boolean>((resolve) => {
          getUserConfirmation(message, (allow) => resolve(allow));
        });
      }

      return true;
    } catch (err) {
      return true;
    }
  };

  return { tabs, tabRefs, beforeTabChange };
}
